import { OrderItemListRows } from '@/data/Content/OrderItemList';
import { useInventory, useInventoryByOrderId } from './_Inventory';
import { ProductAvailabilityData } from '../types/ProductAvailabilityData';

export type OrderItemListRowsWithAvailabilityInternal = (OrderItemListRows[0] & {
	availability?: ProductAvailabilityData;
})[];

export type OrderItemListRowsWithAvailabilityExternal = (OrderItemListRows[0] & {
	availability?: {
		buyable: boolean;
		selectedQuantity: number;
		diff: number;
		currentlyAvailable: number;
		sku: string;
		catEntryId: string;
	};
})[];

export type OrderItemListRowsWithAvailabilityInternalAndExternal = (OrderItemListRows[0] & {
	internalAvailability?: ProductAvailabilityData;
	externalAvailability?: {
		buyable: boolean;
		selectedQuantity: number;
		diff: number;
		currentlyAvailable: number;
		sku: string;
		catEntryId: string;
	};
})[];

export const useOrderItemListRowsAvailability = (orderItemListRows: OrderItemListRows) => {
	const partNumbers = orderItemListRows.map((row) => row.details.partNumber).toString();

	const { availability = [], loading: loadingOrderItemListRowsAvailability } =
		useInventory(partNumbers);

	const orderItemListRowsAvailability =
		orderItemListRows.reduce<OrderItemListRowsWithAvailabilityInternal>((acc, row) => {
			const productId = row.product.id;
			const productAvailability = availability.find((item) => item.productId === productId);

			if (productAvailability && productAvailability.status === false) {
				return acc;
			}

			return [...acc, { ...row, availability: productAvailability }];
		}, []);

	const orderItemListRowsNotAvailable =
		orderItemListRows.reduce<OrderItemListRowsWithAvailabilityInternal>((acc, row) => {
			const productId = row.product.id;
			const productAvailability = availability.find((item) => item.productId === productId);

			if (productAvailability && productAvailability.status === false) {
				return [...acc, { ...row, availability: productAvailability }];
			}

			return acc;
		}, []);

	return {
		orderItemListRowsAvailability,
		orderItemListRowsNotAvailable,
		orderItemListRowsWithAvailability: [
			...orderItemListRowsAvailability,
			...orderItemListRowsNotAvailable,
		],
		loadingOrderItemListRowsAvailability,
	};
};

export const useOrderItemListRowsAvailabilityWithExternal = (
	orderItemListRows: OrderItemListRows,
	orderId?: string
) => {
	const partNumbers = orderItemListRows.map((row) => row.details.partNumber).toString();

	const {
		availability: availabilityInternal = [],
		loading: loadingOrderItemListRowsAvailabilityInternal,
	} = useInventory(partNumbers);

	const {
		availability: availabilityExternal = [],
		loading: loadingOrderItemListRowsAvailabilityExternal,
	} = useInventoryByOrderId(orderId);

	const availabilityExternalNotAvailable = availabilityExternal.filter(
		(item) => item.currentlyAvailable === 0
	);

	const availabilityInternalNotAvailable = availabilityInternal.filter(
		(item) =>
			item.status === false &&
			!availabilityExternalNotAvailable.find((i) => i.catEntryId === item.productId)
	);

	const orderItemListRowsInternalNotAvailable =
		availabilityInternalNotAvailable.reduce<OrderItemListRowsWithAvailabilityInternal>(
			(acc, productAvailability) => {
				const product = orderItemListRows.find(
					(item) => item.product.id === productAvailability.productId
				);

				if (!product) {
					return acc;
				}

				return [...acc, { ...product, availability: productAvailability }];
			},
			[]
		);

	const orderItemListRowsExternalNotAvailable =
		availabilityExternalNotAvailable.reduce<OrderItemListRowsWithAvailabilityExternal>(
			(acc, productAvailability) => {
				const product = orderItemListRows.find(
					(item) => item.product.partNumber === productAvailability.sku
				);

				if (!product) {
					return acc;
				}

				return [...acc, { ...product, availability: productAvailability }];
			},
			[]
		);

	const orderItemListRowsAvailableFilter = orderItemListRows.filter(
		(item) =>
			!orderItemListRowsInternalNotAvailable.find(
				(row) => row.details.partNumber === item.details.partNumber
			) &&
			!orderItemListRowsExternalNotAvailable.find(
				(row) => row.details.partNumber === item.details.partNumber
			)
	);

	const orderItemListRowsAvailability = orderItemListRowsAvailableFilter.map((row) => {
		const productAvailabilityInternal = availabilityInternal.find(
			(item) => item.productId === row.product.id
		);

		const productAvailabilityExternal = availabilityExternal.find(
			(item) => item.sku === row.details.partNumber
		);

		return {
			...row,
			internalAvailability: productAvailabilityInternal,
			externalAvailability: productAvailabilityExternal,
		};
	});

	return {
		orderItemListRowsAvailability,
		orderItemListRowsInternalNotAvailable,
		orderItemListRowsExternalNotAvailable,
		loadingOrderItemListRowsAvailability:
			loadingOrderItemListRowsAvailabilityInternal || loadingOrderItemListRowsAvailabilityExternal,
	};
};
